/**
 * Storage wrappers that never throw.
 *
 * Direct `localStorage`/`sessionStorage` access can throw in hardened or
 * incognito browsers (SecurityError: "Access is denied") and can be `null`
 * in some webviews. These wrappers swallow every failure mode.
 */

interface SafeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function createSafeStorage(
  kind: 'localStorage' | 'sessionStorage',
): SafeStorage {
  const getStorage = (): Storage | null => {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return window[kind] ?? null;
    } catch {
      return null;
    }
  };

  return {
    getItem(key) {
      try {
        return getStorage()?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      try {
        getStorage()?.setItem(key, value);
      } catch {
        // no-op: storage unavailable or quota/security error
      }
    },
    removeItem(key) {
      try {
        getStorage()?.removeItem(key);
      } catch {
        // no-op: storage unavailable
      }
    },
  };
}

export const safeLocalStorage = createSafeStorage('localStorage');
export const safeSessionStorage = createSafeStorage('sessionStorage');
